import {
  UnsupportedFunctionalityError,
  type JSONObject,
  type LanguageModelV3Message,
  type LanguageModelV3Prompt,
  type SharedV3ProviderMetadata,
} from '@ai-sdk/provider';
import {
  convertToBase64,
  parseProviderOptions,
  stripFileExtension,
} from '@ai-sdk/provider-utils';
import {
  BEDROCK_DOCUMENT_MIME_TYPES,
  BEDROCK_IMAGE_MIME_TYPES,
  type BedrockAssistantMessage,
  type BedrockCachePoint,
  type BedrockDocumentFormat,
  type BedrockDocumentMimeType,
  type BedrockImageFormat,
  type BedrockImageMimeType,
  type BedrockMessages,
  type BedrockSystemMessages,
  type BedrockUserMessage,
} from './bedrock-api-types';
import { bedrockReasoningMetadataSchema } from './bedrock-reasoning-metadata';
import { bedrockFilePartProviderOptions } from './bedrock-chat-options';
import { normalizeToolCallId } from './normalize-tool-call-id';

function getCachePoint(
  providerMetadata: SharedV3ProviderMetadata | undefined,
): BedrockCachePoint | undefined {
  const cachePointConfig = providerMetadata?.bedrock?.cachePoint as
    | BedrockCachePoint['cachePoint']
    | undefined;

  if (!cachePointConfig) {
    return undefined;
  }

  return { cachePoint: cachePointConfig };
}

function pushCachePoint(
  content: BedrockUserMessage['content'] | BedrockAssistantMessage['content'],
  providerMetadata: SharedV3ProviderMetadata | undefined,
) {
  const cachePoint = getCachePoint(providerMetadata);
  if (cachePoint) {
    content.push(cachePoint);
  }
}

async function shouldEnableCitations(
  providerMetadata: SharedV3ProviderMetadata | undefined,
): Promise<boolean> {
  const bedrockOptions = await parseProviderOptions({
    provider: 'bedrock',
    providerOptions: providerMetadata,
    schema: bedrockFilePartProviderOptions,
  });

  return bedrockOptions?.citations?.enabled ?? false;
}

export async function convertToBedrockChatMessages(
  prompt: LanguageModelV3Prompt,
  isMistral: boolean = false,
): Promise<{
  system: BedrockSystemMessages;
  messages: BedrockMessages;
}> {
  const blocks = groupIntoBlocks(prompt);

  let system: BedrockSystemMessages = [];
  const messages: BedrockMessages = [];

  let documentCounter = 0;
  const generateDocumentName = () => `document-${++documentCounter}`;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const isLastBlock = i === blocks.length - 1;
    const type = block.type;

    switch (type) {
      case 'system': {
        if (messages.length > 0) {
          throw new UnsupportedFunctionalityError({
            functionality:
              'Multiple system messages that are separated by user/assistant messages',
          });
        }

        for (const message of block.messages) {
          system.push({ text: message.content });
          const cachePoint = getCachePoint(message.providerOptions);
          if (cachePoint) {
            system.push(cachePoint);
          }
        }
        break;
      }

      case 'user': {
        // combines all user and tool messages in this block into a single message:
        const bedrockContent: BedrockUserMessage['content'] = [];

        for (const message of block.messages) {
          const { role, content, providerOptions } = message;
          switch (role) {
            case 'user': {
              for (let j = 0; j < content.length; j++) {
                const part = content[j];

                switch (part.type) {
                  case 'text': {
                    bedrockContent.push({
                      text: part.text,
                    });
                    break;
                  }

                  case 'file': {
                    if (part.data instanceof URL) {
                      // The AI SDK automatically downloads files for user file parts with URLs
                      throw new UnsupportedFunctionalityError({
                        functionality: 'File URL data',
                      });
                    }

                    if (part.mediaType.startsWith('image/')) {
                      bedrockContent.push({
                        image: {
                          format: getBedrockImageFormat(part.mediaType),
                          source: { bytes: convertToBase64(part.data) },
                        },
                      });
                    } else {
                      if (!part.mediaType) {
                        throw new UnsupportedFunctionalityError({
                          functionality: 'file without mime type',
                          message:
                            'File mime type is required in user message part content',
                        });
                      }

                      const enableCitations = await shouldEnableCitations(
                        part.providerOptions,
                      );

                      bedrockContent.push({
                        document: {
                          format: getBedrockDocumentFormat(part.mediaType),
                          name: part.filename
                            ? stripFileExtension(part.filename)
                            : generateDocumentName(),
                          source: { bytes: convertToBase64(part.data) },
                          ...(enableCitations && {
                            citations: { enabled: true },
                          }),
                        },
                      });
                    }

                    break;
                  }
                }

                pushCachePoint(bedrockContent, part.providerOptions);
              }

              break;
            }
            case 'tool': {
              for (const part of content) {
                if (part.type === 'tool-approval-response') {
                  continue;
                }
                let toolResultContent;

                const output = part.output;
                switch (output.type) {
                  case 'content': {
                    toolResultContent = output.value.map(contentPart => {
                      switch (contentPart.type) {
                        case 'text':
                          return { text: contentPart.text };
                        case 'image-data':
                          if (!contentPart.mediaType.startsWith('image/')) {
                            throw new UnsupportedFunctionalityError({
                              functionality: `media type: ${contentPart.mediaType}`,
                            });
                          }

                          const format = getBedrockImageFormat(
                            contentPart.mediaType,
                          );

                          return {
                            image: {
                              format,
                              source: { bytes: contentPart.data },
                            },
                          };
                        default: {
                          throw new UnsupportedFunctionalityError({
                            functionality: `unsupported tool content part type: ${contentPart.type}`,
                          });
                        }
                      }
                    });
                    break;
                  }
                  case 'text':
                  case 'error-text':
                    toolResultContent = [{ text: output.value }];
                    break;
                  case 'execution-denied':
                    toolResultContent = [
                      { text: output.reason ?? 'Tool execution denied.' },
                    ];
                    break;
                  case 'json':
                  case 'error-json':
                  default:
                    toolResultContent = [
                      { text: JSON.stringify(output.value) },
                    ];
                    break;
                }

                bedrockContent.push({
                  toolResult: {
                    toolUseId: normalizeToolCallId(part.toolCallId, isMistral),
                    content: toolResultContent,
                  },
                });
                pushCachePoint(bedrockContent, part.providerOptions);
              }

              break;
            }
            default: {
              const _exhaustiveCheck: never = role;
              throw new Error(`Unsupported role: ${_exhaustiveCheck}`);
            }
          }

          pushCachePoint(bedrockContent, providerOptions);
        }

        messages.push({ role: 'user', content: bedrockContent });

        break;
      }

      case 'assistant': {
        // combines multiple assistant messages in this block into a single message:
        const bedrockContent: BedrockAssistantMessage['content'] = [];

        for (let j = 0; j < block.messages.length; j++) {
          const message = block.messages[j];
          const isLastMessage = j === block.messages.length - 1;
          const { content } = message;
          const hasReasoningBlocks = content.some(
            part => part.type === 'reasoning',
          );

          for (let k = 0; k < content.length; k++) {
            const part = content[k];
            const isLastContentPart = k === content.length - 1;

            switch (part.type) {
              case 'text': {
                // Skip empty text blocks unless reasoning blocks are present
                if (!part.text.trim() && !hasReasoningBlocks) {
                  break;
                }

                bedrockContent.push({
                  text:
                    // trim the last text part if it's the last message in the block
                    // because Bedrock does not allow trailing whitespace
                    // in pre-filled assistant responses
                    trimIfLast(
                      isLastBlock,
                      isLastMessage,
                      isLastContentPart,
                      part.text,
                    ),
                });
                break;
              }

              case 'reasoning': {
                const reasoningMetadata = await parseProviderOptions({
                  provider: 'bedrock',
                  providerOptions: part.providerOptions,
                  schema: bedrockReasoningMetadataSchema,
                });

                if (reasoningMetadata?.signature != null) {
                  // do not trim reasoning text when a signature is present:
                  // the signature validates the exact original bytes
                  bedrockContent.push({
                    reasoningContent: {
                      reasoningText: {
                        text: part.text,
                        signature: reasoningMetadata.signature,
                      },
                    },
                  });
                } else if (reasoningMetadata?.redactedData != null) {
                  bedrockContent.push({
                    reasoningContent: {
                      redactedReasoning: {
                        data: reasoningMetadata.redactedData,
                      },
                    },
                  });
                } else if (
                  part.providerOptions == null ||
                  Object.keys(part.providerOptions).every(
                    k => k === 'bedrock' || k === 'amazonBedrock',
                  )
                ) {
                  // No foreign-provider metadata — preserve text. This covers
                  // the prefill case where the caller hand-crafts a reasoning
                  // block without a signature. Forwarding reasoning that was
                  // signed by a different provider (e.g. anthropic) would
                  // cause Bedrock to reject with
                  // `thinking.signature: Field required`, so we drop those.
                  // trim the last text part if it's the last message in the
                  // block because Bedrock does not allow trailing whitespace
                  // in pre-filled assistant responses
                  bedrockContent.push({
                    reasoningContent: {
                      reasoningText: {
                        text: trimIfLast(
                          isLastBlock,
                          isLastMessage,
                          isLastContentPart,
                          part.text,
                        ),
                      },
                    },
                  });
                }

                break;
              }

              case 'tool-call': {
                bedrockContent.push({
                  toolUse: {
                    toolUseId: normalizeToolCallId(part.toolCallId, isMistral),
                    name: part.toolName,
                    input: part.input as JSONObject,
                  },
                });
                break;
              }
            }

            pushCachePoint(bedrockContent, part.providerOptions);
          }
          pushCachePoint(bedrockContent, message.providerOptions);
        }

        messages.push({ role: 'assistant', content: bedrockContent });

        break;
      }

      default: {
        const _exhaustiveCheck: never = type;
        throw new Error(`Unsupported type: ${_exhaustiveCheck}`);
      }
    }
  }

  return { system, messages };
}

function isBedrockImageFormat(format: string): format is BedrockImageFormat {
  return Object.values(BEDROCK_IMAGE_MIME_TYPES).includes(
    format as BedrockImageFormat,
  );
}

function getBedrockImageFormat(mimeType?: string): BedrockImageFormat {
  if (!mimeType) {
    throw new UnsupportedFunctionalityError({
      functionality: 'image without mime type',
      message: 'Image mime type is required in user message part content',
    });
  }

  const format = BEDROCK_IMAGE_MIME_TYPES[mimeType as BedrockImageMimeType];
  if (!format) {
    throw new UnsupportedFunctionalityError({
      functionality: `image mime type: ${mimeType}`,
      message: `Unsupported image mime type: ${mimeType}, expected one of: ${Object.keys(BEDROCK_IMAGE_MIME_TYPES).join(', ')}`,
    });
  }

  return format;
}

function getBedrockDocumentFormat(mimeType: string): BedrockDocumentFormat {
  const format =
    BEDROCK_DOCUMENT_MIME_TYPES[mimeType as BedrockDocumentMimeType];
  if (!format) {
    throw new UnsupportedFunctionalityError({
      functionality: `file mime type: ${mimeType}`,
      message: `Unsupported file mime type: ${mimeType}, expected one of: ${Object.keys(BEDROCK_DOCUMENT_MIME_TYPES).join(', ')}`,
    });
  }
  return format;
}

function trimIfLast(
  isLastBlock: boolean,
  isLastMessage: boolean,
  isLastContentPart: boolean,
  text: string,
) {
  return isLastBlock && isLastMessage && isLastContentPart ? text.trim() : text;
}

type SystemBlock = {
  type: 'system';
  messages: Array<LanguageModelV3Message & { role: 'system' }>;
};
type AssistantBlock = {
  type: 'assistant';
  messages: Array<LanguageModelV3Message & { role: 'assistant' }>;
};
type UserBlock = {
  type: 'user';
  messages: Array<LanguageModelV3Message & { role: 'user' | 'tool' }>;
};

function groupIntoBlocks(
  prompt: LanguageModelV3Prompt,
): Array<SystemBlock | AssistantBlock | UserBlock> {
  const blocks: Array<SystemBlock | AssistantBlock | UserBlock> = [];
  let currentBlock: SystemBlock | AssistantBlock | UserBlock | undefined =
    undefined;

  for (const message of prompt) {
    const { role } = message;
    switch (role) {
      case 'system': {
        if (currentBlock?.type !== 'system') {
          currentBlock = { type: 'system', messages: [] };
          blocks.push(currentBlock);
        }

        currentBlock.messages.push(message);
        break;
      }
      case 'assistant': {
        if (currentBlock?.type !== 'assistant') {
          currentBlock = { type: 'assistant', messages: [] };
          blocks.push(currentBlock);
        }

        currentBlock.messages.push(message);
        break;
      }
      case 'user': {
        if (currentBlock?.type !== 'user') {
          currentBlock = { type: 'user', messages: [] };
          blocks.push(currentBlock);
        }

        currentBlock.messages.push(message);
        break;
      }
      case 'tool': {
        if (currentBlock?.type !== 'user') {
          currentBlock = { type: 'user', messages: [] };
          blocks.push(currentBlock);
        }

        currentBlock.messages.push(message);
        break;
      }
      default: {
        const _exhaustiveCheck: never = role;
        throw new Error(`Unsupported role: ${_exhaustiveCheck}`);
      }
    }
  }

  return blocks;
}
